import React from 'react';
import { useTranslation } from 'react-i18next';

const cellVal = (row, item) => {
  const v = row?.[item?.key];
  if (v == null) return '';
  if (item?.type === 'BOOLEAN' || typeof v === 'boolean') return v ? '■' : '□';
  return String(v);
};

/** 동의 미리보기 전용 — 텍스트만 */
function consentOrgConsentLine(row, item, labelFallback) {
  const label = item?.label || labelFallback || '';
  const v = row?.[item?.key];
  if (v == null || v === '') return `${label}    -`;
  if (item?.type === 'BOOLEAN' || typeof v === 'boolean') {
    if (typeof v === 'boolean') return `${label}    ${v ? '동의' : '미동의'}`;
    if (v === true || v === 1) return `${label}    동의`;
    if (v === false || v === 0) return `${label}    미동의`;
    const su0 = String(v).trim().toUpperCase();
    if (su0 === 'N' || su0 === 'NO' || su0 === 'FALSE') return `${label}    미동의`;
    if (su0 === 'Y' || su0 === 'YES' || su0 === 'TRUE') return `${label}    동의`;
    return `${label}    ${String(v)}`;
  }
  const dt = String(item?.dataType || '').toUpperCase().replace(/[\s-]/g, '_');
  if (dt === 'ENUM_TYPE' || dt === 'ENUM') {
    const opts = item?.options || item.enumOptions || [];
    const found = opts.find((o) => String(o?.value ?? o?.key ?? '') === String(v));
    if (found?.label != null) return `${label}    ${String(found.label)}`;
  }
  const su = String(v).trim().toUpperCase();
  if (su === 'Y' || su === 'YES' || su === 'TRUE') return `${label}    동의`;
  if (su === 'N' || su === 'NO' || su === 'FALSE') return `${label}    미동의`;
  return `${label}    ${String(v)}`;
}

/** 구 양식 키용 — 레이아웃 label 이 있으면 항상 우선 */
const CONSENT_LABEL_FALLBACK_BY_KEY = {
  privacy_agreement_1: '개인정보의 제공 및 활용 동의',
  privacy_agreement_2: '고유식별정보의 제공 및 활용 동의',
  privacy_agreement_3: '개인정보의 제3자 제공동의',
};

const mergeFirstRowsAll = (layoutRows) => {
  const merged = {};
  Object.keys(layoutRows || {}).forEach((k) => {
    const rows = layoutRows[k];
    if (!Array.isArray(rows) || !rows[0]) return;
    Object.assign(merged, rows[0]);
  });
  return merged;
};

const formatKoreanDate = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[1]}. ${m[2]}. ${m[3]}.`;
  return s;
};

const itemByKey = (items, key) => (items || []).find((it) => String(it?.key) === String(key));
const splitItems = (items) => {
  const arr = Array.isArray(items) ? items : [];
  return {
    base: arr.filter((it) => !it?.__custom),
    extra: arr.filter((it) => !!it?.__custom),
  };
};

const makeFull = (a) => ({ kind: 'full', a });
const makePair = (a, b) => ({ kind: 'pair', a, b });

// 캡쳐 화면과 동일한 "상단(로고+기업정보)" 템플릿(행/페어링 고정)
const buildTopSectionTemplate = (baseNoLogo) => {
  const keys = new Set((baseNoLogo || []).map((it) => it?.key).filter(Boolean));
  const hasAll = ['company_name', 'founded_year', 'org_type', 'address', 'ceo_name', 'contact_person', 'phone', 'email']
    .every((k) => keys.has(k));

  if (!hasAll) {
    // 구조가 예상과 다르면, 표 스타일은 유지하되 단독(full)로 순서대로 출력
    return { lines: (baseNoLogo || []).filter(it => it?.key).map((it) => makeFull(it)), extras: [] };
  }

  const usedKeys = new Set(['company_name', 'founded_year', 'org_type', 'address', 'ceo_name', 'contact_person', 'phone', 'email']);
  const lines = [
    makeFull(itemByKey(baseNoLogo, 'company_name')),
    makePair(itemByKey(baseNoLogo, 'founded_year'), itemByKey(baseNoLogo, 'org_type')),
    makeFull(itemByKey(baseNoLogo, 'address')),
    makeFull(itemByKey(baseNoLogo, 'ceo_name')),
    makePair(itemByKey(baseNoLogo, 'contact_person'), itemByKey(baseNoLogo, 'phone')),
    makeFull(itemByKey(baseNoLogo, 'email')),
  ].filter((l) => l?.a?.key || l?.b?.key);

  const extras = (baseNoLogo || []).filter((it) => it?.key && !usedKeys.has(it.key));
  return { lines, extras };
};

const renderSectionRows = ({ rows, categoryTitle, categoryRowSpan, lines, extras, includeCategoryCell }) => {
  const tbody = [];
  const perRowCount = (lines?.length || 0) + (extras?.length || 0);
  const totalRowCount = (rows?.length || 0) * perRowCount;
  if (!totalRowCount) return tbody;

  (rows || []).forEach((row, rIdx) => {
    (lines || []).forEach((fr, frIdx) => {
      const isFirst = rIdx === 0 && frIdx === 0;
      if (fr.kind === 'pair') {
        tbody.push(
          <tr key={`sec-${categoryTitle || 'none'}-r${rIdx}-l${frIdx}`}>
            {includeCategoryCell && isFirst && (
              <td className="poc-cat" rowSpan={categoryRowSpan ?? totalRowCount}>
                {categoryTitle}
              </td>
            )}
            <td className="poc-label">{fr.a?.label}</td>
            <td className="poc-value">{cellVal(row, fr.a)}</td>
            <td className="poc-label">{fr.b?.label}</td>
            <td className="poc-value">{cellVal(row, fr.b)}</td>
          </tr>
        );
      } else {
        tbody.push(
          <tr key={`sec-${categoryTitle || 'none'}-r${rIdx}-l${frIdx}`}>
            {includeCategoryCell && isFirst && (
              <td className="poc-cat" rowSpan={categoryRowSpan ?? totalRowCount}>
                {categoryTitle}
              </td>
            )}
            <td className="poc-label">{fr.a?.label}</td>
            <td className="poc-value" colSpan={3}>{cellVal(row, fr.a)}</td>
          </tr>
        );
      }
    });

    (extras || []).forEach((it, eIdx) => {
      const key = it?.key || `extra-${eIdx}`;
      tbody.push(
        <tr key={`sec-${categoryTitle || 'none'}-r${rIdx}-x${key}`}>
          <td className="poc-label">{it?.label}</td>
          <td className="poc-value" colSpan={3}>{cellVal(row, it)}</td>
        </tr>
      );
    });
  });

  return tbody;
};

export default function CompanyOrgSignupPreview({ signatureDataUrl, structureJson, dataJson }) {
  const { t } = useTranslation();
  const signatureLabel = t('popup.previewPopup.signature');
  const signatureAlt = t('popup.previewPopup.signatureAlt');
  const layout = Array.isArray(structureJson?.layout) ? structureJson.layout : [];
  const layoutRows = dataJson?.layoutRows || {};

  const sec0 = layout[0] || null;
  const sec1 = layout[1] || null;
  const sec2 = layout[2] || null;
  const sec3 = layout[3] || null;
  const dividerIdx = layout.findIndex(b => b?.type === 'DIVIDER');
  const divider = dividerIdx >= 0 ? layout[dividerIdx] : null;

  const rows0 = Array.isArray(layoutRows?.[0]) ? layoutRows[0] : [];
  const rows1 = Array.isArray(layoutRows?.[1]) ? layoutRows[1] : [];
  const rows2 = Array.isArray(layoutRows?.[2]) ? layoutRows[2] : [];
  const rows3 = Array.isArray(layoutRows?.[3]) ? layoutRows[3] : [];
  const dividerRow0 = dividerIdx >= 0 && Array.isArray(layoutRows?.[dividerIdx]) ? (layoutRows[dividerIdx]?.[0] || {}) : {};

  const items0 = sec0?.type === 'SECTION' ? (sec0.fields || []) : [];
  const items1 = sec1?.type === 'SECTION' ? (sec1.fields || []) : [];
  const items2 = sec2?.type === 'SECTION' ? (sec2.fields || []) : [];
  const items3 = sec3?.type === 'SECTION' ? (sec3.fields || []) : [];
  const dividerItems = divider?.type === 'DIVIDER' ? (divider.items || []) : [];

  const consentItems = dividerItems.filter((it) => {
    if (!it?.key) return false;
    if (it.type === 'BOOLEAN') return true;
    const dt = String(it.dataType || '').toUpperCase().replace(/[\s-]/g, '_');
    if (dt !== 'ENUM_TYPE' && dt !== 'ENUM') return false;
    const vals = (it.options || it.enumOptions || []).map((o) => String(o?.value ?? o?.key ?? '').toUpperCase());
    return vals.includes('Y') && vals.includes('N');
  });
  const mergedConsentRow = mergeFirstRowsAll(layoutRows);

  const joinDateItem = itemByKey(dividerItems, 'join_date') || itemByKey(dividerItems, 'written_date');
  const joinDate = formatKoreanDate(joinDateItem ? dividerRow0?.[joinDateItem.key] : '');
  const companyNameFooterItem = itemByKey(dividerItems, 'company_name_footer');
  const companyNameFooter = companyNameFooterItem ? String(dividerRow0?.[companyNameFooterItem.key] || '').trim() : '';

  const row0First = rows0?.[0] || {};
  const companyName = String(row0First?.company_name || '').trim();
  const contactName = String(row0First?.contact_person || '').trim();
  const logoDataUrl = String(row0First?.company_logo || '').trim();

  // 0) 상단 섹션(로고 + 기업정보) 템플릿 + 사용자 추가 열 확장
  const { base: items0BaseAll, extra: items0Extra } = splitItems(items0);
  const logoItem = itemByKey(items0BaseAll, 'company_logo');
  const items0BaseNoLogo = (items0BaseAll || []).filter((it) => String(it?.key) !== 'company_logo');
  const top = buildTopSectionTemplate(items0BaseNoLogo);
  // 상단 섹션 extras는 "템플릿에서 사용되지 않은 base 항목" + "사용자 추가(__custom) 항목" 모두 반영
  const topExtras = [...(top.extras || []), ...(items0Extra || [])].filter(Boolean);
  const topPerRowCount = (top.lines?.length || 0) + (topExtras?.length || 0);
  const topRowSpan = (rows0?.length || 0) * topPerRowCount;

  // 1~3) 나머지 섹션은 기본 구조 순서대로(full) + 사용자 추가 열을 하단에 확장
  const sec1Split = splitItems(items1);
  const sec2Split = splitItems(items2);
  const sec3Split = splitItems(items3);

  const sec1Lines = (sec1Split.base || []).filter(it => it?.key).map((it) => makeFull(it));
  const sec2Lines = (sec2Split.base || []).filter(it => it?.key).map((it) => makeFull(it));
  const sec3Lines = (sec3Split.base || []).filter(it => it?.key).map((it) => makeFull(it));

  const sec1PerRow = (sec1Lines.length || 0) + (sec1Split.extra.length || 0);
  const sec2PerRow = (sec2Lines.length || 0) + (sec2Split.extra.length || 0);
  const sec3PerRow = (sec3Lines.length || 0) + (sec3Split.extra.length || 0);

  const sec1RowSpan = (rows1.length || 0) * sec1PerRow;
  const sec2RowSpan = (rows2.length || 0) * sec2PerRow;
  const sec3RowSpan = (rows3.length || 0) * sec3PerRow;

  return (
    <div className="poc-preview-shell dfb-preview-shell">
      <div className="poc-preview-page dfb-preview-page">
        <div className="poc-preview">
          <div className="poc-title">기관·기업 회원가입 신청서</div>
          <div className="poc-title-line"></div>

          <div className="org-desc">
            GLOW Platform의 설립목적과 취지에 찬동하여 기관·기업회원으로 가입하고 회원으로서의 제의무를 성실히 수행하고자 하오니 가입을 승낙하여 주시기 바랍니다.
          </div>
          <div className="org-dotted"></div>

          <table className="poc-table">
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '26%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '24%' }} />
            </colgroup>
            <tbody>
              {/* 0) 상단 기업정보 + 로고 (기존 템플릿 유지 + 행/열 확장) */}
              {(rows0 || []).flatMap((row, rIdx) => {
                const rows = [];
                (top.lines || []).forEach((fr, frIdx) => {
                  const isFirstRow = rIdx === 0 && frIdx === 0;
                  if (fr.kind === 'pair') {
                    rows.push(
                      <tr key={`top-r${rIdx}-l${frIdx}`}>
                        {isFirstRow && (
                          <td className="poc-cat org-logo-cell" rowSpan={topRowSpan || 6}>
                            {logoDataUrl ? (
                              <img className="org-logo-img" src={logoDataUrl} alt="company-logo" />
                            ) : (
                              <>{logoItem?.label || <>기업<br />로고 이미지<br />(첨부)</>}</>
                            )}
                          </td>
                        )}
                        <td className="poc-label">{fr.a?.label}</td>
                        <td className="poc-value">{cellVal(row, fr.a)}</td>
                        <td className="poc-label">{fr.b?.label}</td>
                        <td className="poc-value">{cellVal(row, fr.b)}</td>
                      </tr>
                    );
                  } else {
                    rows.push(
                      <tr key={`top-r${rIdx}-l${frIdx}`}>
                        {isFirstRow && (
                          <td className="poc-cat org-logo-cell" rowSpan={topRowSpan || 6}>
                            {logoDataUrl ? (
                              <img className="org-logo-img" src={logoDataUrl} alt="company-logo" />
                            ) : (
                              <>{logoItem?.label || <>기업<br />로고 이미지<br />(첨부)</>}</>
                            )}
                          </td>
                        )}
                        <td className="poc-label">{fr.a?.label}</td>
                        <td className="poc-value" colSpan={3}>{cellVal(row, fr.a)}</td>
                      </tr>
                    );
                  }
                });

                (topExtras || []).forEach((it, eIdx) => {
                  const key = it?.key || `extra-${eIdx}`;
                  rows.push(
                    <tr key={`top-r${rIdx}-x${key}`}>
                      <td className="poc-label">{it?.label}</td>
                      <td className="poc-value" colSpan={3}>{cellVal(row, it)}</td>
                    </tr>
                  );
                });

                return rows;
              })}

              {/* 1) 산업분야 */}
              {renderSectionRows({
                rows: rows1,
                categoryTitle: sec1?.title || '산업분야',
                categoryRowSpan: sec1RowSpan || undefined,
                lines: sec1Lines,
                extras: sec1Split.extra,
                includeCategoryCell: true,
              })}

              {/* 2) 수요·협력정보 */}
              {renderSectionRows({
                rows: rows2,
                categoryTitle: sec2?.title || '수요·협력정보',
                categoryRowSpan: sec2RowSpan || undefined,
                lines: sec2Lines,
                extras: sec2Split.extra,
                includeCategoryCell: true,
              })}

              {/* 3) 추가 항목 */}
              {renderSectionRows({
                rows: rows3,
                categoryTitle: sec3?.title || '추가 항목',
                categoryRowSpan: sec3RowSpan || undefined,
                lines: sec3Lines,
                extras: sec3Split.extra,
                includeCategoryCell: true,
              })}

              {/* 동의 */}
              {(consentItems?.length || 0) > 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 0 }}>
                    <div className="poc-consent-lines">
                      {(consentItems || []).map((it) => (
                        <div key={`org-consent-${it.key}`} className="line">
                          {consentOrgConsentLine(mergedConsentRow, it, CONSENT_LABEL_FALLBACK_BY_KEY[it.key])}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* footer(가입일/기업명/로고/작성자/서명) */}
          <div className="poc-footer">
            <div className="poc-footer-date">
              가입일 {joinDate}
              {(companyNameFooter || companyName) && (
                <span className="ms-2">기업명 {companyNameFooter || companyName}</span>
              )}
            </div>

            <div className="poc-footer-row">
              <div className="poc-logo">
                <div className="mark">ⓘ</div>
                <div className="txt">
                  <div className="ko">한국외국어대학교 G-RISE 사업단</div>
                  <div className="en">HUFS G-Regional Innovation System & Education Center</div>
                </div>
              </div>

              <div className="poc-author">
                <span>
                  기관명·기업명: {companyName || '-'}&nbsp;&nbsp;{contactName}
                </span>
                <div className="poc-author-signature-overlap">
                  {signatureDataUrl && <img src={signatureDataUrl} alt={signatureAlt} />}
                  <span className="poc-author-signature-label">{signatureLabel}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

